import React from 'react';

interface StarDecorationsProps {
  count?: number;
  density?: 'low' | 'medium' | 'high';
  includeEmojis?: boolean;
  includeCrosses?: boolean;
}

export function StarDecorations({ 
  count = 15, 
  density = 'medium',
  includeEmojis = true,
  includeCrosses = true 
}: StarDecorationsProps) {
  
  // Respeitar preferência de redução de movimento
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Posições pré-definidas para evitar classes dinâmicas
  const positions = [
    // Top positions
    'top-10 right-20', 'top-16 right-32', 'top-24 right-16', 'top-32 right-48',
    'top-20 left-16', 'top-28 left-32', 'top-36 left-20', 'top-44 left-40',
    
    // Middle positions
    'top-40 right-24', 'top-48 right-40', 'top-56 right-16', 'top-64 right-32',
    'top-52 left-24', 'top-60 left-16', 'top-72 left-36',
    
    // Bottom positions
    'bottom-20 right-28', 'bottom-32 right-16', 'bottom-40 right-40', 'bottom-48 right-24',
    'bottom-24 left-20', 'bottom-36 left-32', 'bottom-44 left-16', 'bottom-52 left-28'
  ];

  const sizes = ['w-1 h-1', 'w-2 h-2', 'w-3 h-3', 'w-4 h-4'];
  const colors = ['bg-white-bb', 'bg-yellow-bb', 'bg-blue-bb-6'];
  const animations = ['animate-pulse', 'animate-ping', 'animate-bounce', ''];
  const opacities = ['opacity-40', 'opacity-50', 'opacity-60', 'opacity-70', 'opacity-80', 'opacity-90'];

  // Gerar decorações
  const generateDecorations = () => {
    const decorations = [];
    
    // Círculos coloridos
    const circleCount = Math.floor(count * 0.7);
    for (let i = 0; i < circleCount; i++) {
      const position = positions[i % positions.length];
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const animation = prefersReducedMotion ? '' : animations[Math.floor(Math.random() * animations.length)];
      const opacity = opacities[Math.floor(Math.random() * opacities.length)];
      const randomDelay = Math.random() * 3; // Delay aleatório entre 0 e 3 segundos
      const randomDuration = 1 + Math.random() * 2; // Duração aleatória entre 1 e 3 segundos

      decorations.push(
        <div
          key={`circle-${i}`}
          className={`absolute ${position} ${size} ${color} rounded-full ${opacity} ${animation}`}
          style={{
            animationDelay: `${randomDelay}s`,
            animationDuration: `${randomDuration}s`
          }}
        />
      );
    }

    // Emojis de estrelas
    if (includeEmojis) {
      const emojis = ['✨', '⭐', '✦', '💫', '🌟'];
      const emojiCount = Math.floor(count * 0.2);
      
      for (let i = 0; i < emojiCount; i++) {
        const position = positions[(circleCount + i) % positions.length];
        const emoji = emojis[Math.floor(Math.random() * emojis.length)];
        const animation = prefersReducedMotion ? '' : animations.slice(0, 3)[Math.floor(Math.random() * 3)];
        const opacity = opacities.slice(0, 4)[Math.floor(Math.random() * 4)];
        const randomDelay = Math.random() * 3;
        const randomDuration = 1 + Math.random() * 2;

        decorations.push(
          <div
            key={`emoji-${i}`}
            className={`absolute ${position} text-lg ${opacity} ${animation} pointer-events-none select-none`}
            style={{
              animationDelay: `${randomDelay}s`,
              animationDuration: `${randomDuration}s`
            }}
          >
            {emoji}
          </div>
        );
      }
    }

    // Cruzes flutuantes
    if (includeCrosses) {
      const crossCount = Math.floor(count * 0.1);
      
      for (let i = 0; i < crossCount; i++) {
        const position = positions[(circleCount + Math.floor(count * 0.2) + i) % positions.length];
        const color = ['bg-white-bb', 'bg-yellow-bb'][Math.floor(Math.random() * 2)];
        const animation = prefersReducedMotion ? '' : animations.slice(0, 3)[Math.floor(Math.random() * 3)];
        const opacity = opacities.slice(2, 5)[Math.floor(Math.random() * 3)];

        decorations.push(
          <div key={`cross-${i}`} className={`absolute ${position} ${animation}`}>
            <div className={`w-3 h-0.5 ${color} ${opacity} rotate-45`}></div>
            <div className={`w-0.5 h-3 ${color} ${opacity} rotate-45 absolute top-0 left-1`}></div>
          </div>
        );
      }
    }

    return decorations;
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {generateDecorations()}
    </div>
  );
}